// D:\directiveai-react\frontend\src\components\home\HomeInput\components\PromptCard.jsx

import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { Check } from 'lucide-react';
import { Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getStyleComponent } from '../styles';
import {
  PromptCardItem,
  PromptIconWrapper,
  PromptText,
  CheckmarkIcon
} from '../HomeInputStyles';
import { INTERFACE_STYLES } from '../styles';

const PromptCard = ({
  Icon,
  text,
  action,
  isProOnly = false,
  isPro = false,
  onClick,
  selected = false,
  interfaceStyle
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDisabled = isProOnly && !isPro;
  const isAssistantStyle = interfaceStyle === INTERFACE_STYLES.ASSISTANT;
  const isSlidingCard = action === 'sliding';

  // Obtener estilos del componente de estilo correspondiente
  const styleComponent = getStyleComponent(interfaceStyle);
  const styleOverrides = styleComponent.getPromptCardStyles(theme);

  const handleClick = () => {
    if (!isDisabled) {
      onClick(text);
    }
  };

  // Aplicar estilos consistentes para tarjetas en la interfaz Assistant
  // pero quitar sombras para sliding cards
  const cardStyles = isAssistantStyle 
    ? (isSlidingCard 
        ? {
            ...styleOverrides,
            boxShadow: 'none', // Eliminar sombras para tarjetas deslizantes
          }
        : styleOverrides)
    : {};

  return (
    <Tooltip
      title={isDisabled ? t('homeInput.proFeature') : ''}
      placement="bottom"
      arrow
    >
      <motion.div
        whileHover={!isDisabled ? { scale: 1.02, y: -2 } : {}}
        whileTap={!isDisabled ? { scale: 0.98 } : {}}
        transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ display: 'inline-block', width: '100%' }}
      >
        <PromptCardItem
          onClick={handleClick}
          disabled={isDisabled}
          selected={selected && !isSlidingCard}
          sx={{
            // Aplicar los estilos consistentes (sin sombras para sliding)
            ...cardStyles,
            // Añadir estilos adicionales para tarjetas deslizantes si es necesario
            ...(isAssistantStyle && isSlidingCard ? {
              width: '180px',
              minWidth: '180px',
              margin: '0 4px',
              padding: '10px 16px',
              // Asegurar que no tenga borde de selección para sliding cards
              border: theme.palette.actualMode === 'wallpaper'
                ? `1px solid rgba(128, 128, 128, 0.3)`
                : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            } : {})
          }}
        >
        {/* Mostrar el icono solo si NO es una tarjeta deslizante o no es estilo Assistant */}
        {(!isSlidingCard || !isAssistantStyle) && (
          <PromptIconWrapper>
            <Icon
              size={16}
              color={theme.palette.actualMode === 'wallpaper'
                ? '#FFFFFF'
                : theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'
              }
            />
          </PromptIconWrapper>
        )}
        
        <PromptText 
          selected={selected && !isSlidingCard}
          sx={{
            ...(isAssistantStyle && isSlidingCard ? {
              textAlign: 'center',
              fontSize: '13px',
            } : {})
          }}
        >
          {text}
        </PromptText>
        
        {/* Mostrar el icono de selección solo si está seleccionado Y NO es una tarjeta deslizante */}
        {selected && !isSlidingCard && (
          <CheckmarkIcon>
            <Check />
          </CheckmarkIcon>
        )}
        </PromptCardItem>
      </motion.div>
    </Tooltip>
  );
};

PromptCard.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  text: PropTypes.string.isRequired,
  action: PropTypes.string,
  isProOnly: PropTypes.bool,
  isPro: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  interfaceStyle: PropTypes.string.isRequired,
};

export default React.memo(PromptCard);